import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <Camera className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-slate-800">FlockTracker</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8">
            <h1 className="text-4xl font-bold text-slate-800 mb-6">Terms of Use</h1>
            <p className="text-sm text-slate-500 mb-8">Effective Date: November 6, 2025</p>

            <div className="space-y-6 text-slate-700">
              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">1. Acceptance of Terms</h2>
                <p>By accessing and using FlockTracker.org ("the Service"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Service.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">2. Purpose of Service</h2>
                <p>FlockTracker is a community-driven platform that allows users to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Report and track surveillance camera locations in their communities</li>
                  <li>Share stories and experiences related to privacy and surveillance</li>
                  <li>Engage in discussions about community safety and privacy concerns</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">3. User Responsibilities</h2>
                <p>When using FlockTracker, you agree to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Provide accurate information about camera locations</li>
                  <li>Not submit false, misleading, or defamatory content</li>
                  <li>Respect the privacy and rights of others</li>
                  <li>Not use the Service for any illegal purposes</li>
                  <li>Not harass, threaten, or harm other users</li>
                  <li>Comply with all applicable local, state, and federal laws</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">4. Content Moderation</h2>
                <p>All user-submitted stories are subject to moderator approval before publication. We reserve the right to remove or reject any content that:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Violates these Terms of Use</li>
                  <li>Contains false or misleading information</li>
                  <li>Is defamatory, obscene, or offensive</li>
                  <li>Violates intellectual property rights</li>
                  <li>Poses a security or safety risk</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">5. User Content License</h2>
                <p>By submitting content to FlockTracker, you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content on the platform. You retain ownership of your content.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">6. Accuracy and Liability</h2>
                <p>FlockTracker is a community-contributed platform. We do not guarantee the accuracy of camera locations or user-submitted information. The Service is provided "as is" without warranties of any kind.</p>
                <p className="mt-2">We are not liable for any damages arising from the use of this Service or reliance on information provided by users.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">7. Account Termination</h2>
                <p>We reserve the right to suspend or terminate user accounts that violate these Terms of Use or engage in harmful behavior on the platform.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">8. Changes to Terms</h2>
                <p>We may update these Terms of Use from time to time. Continued use of the Service after changes constitutes acceptance of the updated terms.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">9. Contact Information</h2>
                <p>For questions about these Terms of Use, please contact us through our website.</p>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}